import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import FormField from "components/v2/FormField";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { IoIosArrowForward } from "react-icons/io";
import useUpdatePhysicalBook from "api/BookResource/useUpdatePhysicalBook";
import { MAX_IMAGE_SIZE } from "utils";
import { HiPencil } from "react-icons/hi";
import RichTextEditor from "components/RichTextEditor";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";

const UpdatePhysicalBook = ({ book }) => {
	const [visible, setVisible] = useState(false);

	// Fetch data for dropdowns
	const { data: categories, isLoading: isCategoriesLoading } =
		useFetchCategories("yes");
	const { data: genres, isLoading: isGenresLoading } = useGenres("yes");

	const initialValues = {
		title: book?.title || "",
		author: book?.author || "",
		isbn: book?.isbn || "",
		description: book?.description || "",
		category_id: book?.category?.id || "",
		genres: book?.genres?.[0]?.id || "",
		physical_usd_price: book?.physical_usd_price || "",
		book_cover: null,
	};

	const validate = Yup.object().shape({
		title: Yup.string().required("Book title is required"),
		author: Yup.string().required("Author is required"),
		category_id: Yup.number().required("Category is required"),
		// genres: Yup.string().required("Genre is required"),
		physical_usd_price: Yup.number()
			.typeError("Price must be a number")
			.min(0, "Price must be greater than or equal to 0")
			.required("Price is required"),
		book_cover: Yup.mixed()
			.nullable()
			.test({
				name: "fileSize",
				test: (value) => !value || value.size / 1024 / 1024 <= MAX_IMAGE_SIZE,
				message: `Image size should not exceed ${MAX_IMAGE_SIZE}MB`,
			})
			.test({
				name: "mimeType",
				test: (value) =>
					!value ||
					["image/jpeg", "image/png", "image/svg+xml", "image/webp"].includes(
						value.type
					),
				message:
					"Only images with these file extensions are allowed: jpeg, jpg, png, svg, webp",
			}),
	});

	const { mutateAsync, isLoading } = useUpdatePhysicalBook(book?.id);

	const handleSubmit = async (data) => {
		await mutateAsync(data);
		setVisible(false);
	};

	// Prepare options for dropdowns
	const categoryOptions =
		categories?.data?.map((category) => ({
			label: category.name,
			value: category.id,
		})) || [];

	const genreOptions =
		genres?.data?.data?.map((genre) => ({
			label: genre.name,
			value: genre.id,
		})) || [];

	return (
		<>
			<button
				type="button"
				onClick={() => setVisible(true)}
				className="p-2 text-akgreener  rounded-full transition-colors"
				title="Update book"
			>
				<HiPencil size={20} />
			</button>

			<Dialog
				visible={visible}
				onHide={() => setVisible(false)}
				header="Update book"
				style={{ minWidth: "500px", maxWidth: "600px" }}
				// modal
				className="font-sans"
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validate}
					onSubmit={handleSubmit}
				>
					{({ setFieldValue, values, errors, touched }) => (
						<Form className="space-y-6 pt-10">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormField
									type="text"
									name="title"
									label="Book Title *"
								/>
								<FormField
									type="text"
									name="author"
									label="Author *"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormField
									type="text"
									name="isbn"
									label="ISBN Number"
								/>
								<FormField
									type="number"
									name="physical_usd_price"
									label="USD Price *"
									placeholder="0.00"
									step="0.01"
									min="0"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<FormField
									type="select"
									name="category_id"
									label="Category *"
									options={categoryOptions}
									isLoading={isCategoriesLoading}
								/>

								<FormField
									type="select"
									name="genres"
									label="Genre"
									options={genreOptions}
									isLoading={isGenresLoading}
								/>
							</div>

							<div className="space-y-2">
								<p className="text-gray-500">Book summary</p>
								<RichTextEditor
									value={values.description}
									onTextChange={(e) =>
										setFieldValue("description", e.htmlValue)
									}
									error={
										!!(touched.description && errors.description) &&
										errors.description
									}
								/>
							</div>

							<div>
								<FormField
									type="file"
									name="book_cover"
									value={null}
									label="Book Cover"
									style={{
										paddingLeft: `${!values?.book_cover ? "90px" : ""} `,
									}}
									onChange={(event) =>
										setFieldValue("book_cover", event.target.files[0])
									}
								/>
								{book?.book_cover?.url && (
									<div className="mt-2">
										<p className="text-sm text-gray-600 mb-2">Current cover:</p>
										<img
											src={book.book_cover.url}
											alt="Current book cover"
											className="w-24 h-32 object-cover rounded border"
										/>
									</div>
								)}
							</div>

							<div className="flex justify-end gap-3 pt-4 border-t">
								<button
									type="button"
									onClick={() => setVisible(false)}
									className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={isLoading}
									className="px-4 py-2 bg-akgreener text-white rounded-lg hover:bg-akgreenest transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
								>
									{isLoading ? (
										<>
											<AiOutlineLoading3Quarters
												size={16}
												className="animate-spin"
											/>
											Updating...
										</>
									) : (
										<>
											Update Book
											<IoIosArrowForward size={16} />
										</>
									)}
								</button>
							</div>
						</Form>
					)}
				</Formik>
			</Dialog>
		</>
	);
};

export default UpdatePhysicalBook;
